import { type CSSProperties, type ReactNode } from 'react';
import { ArrowRight, Hash, Plus, Search, Send, Smile, Users } from 'lucide-react';
import { MessageCircle, Pin } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Avatar } from '@/components/ui';
import { ChannelFx } from '@/components/ChannelFx';
import { BannerCoverRender } from '@/components/theme/BannerLayerRender';
import { getTheme } from '@/features/theme/themes';
import { themeAssetUrl } from '@/features/theme/themeAssets';
import { fmtCount } from '@/features/communities/community-view/channels';
import { displayName } from '@/features/communities/community-view/cvHelpers';
import type { ViewMember, ViewMessage } from '@/features/communities/api/communityView';
import { iconById } from '@/lib/communityIcons';
import {
  bgTintOpacity,
  cardClass,
  dimOverlay,
  fontClass,
  groupStudioChannels,
  radiusClass,
  railWidthPx,
  rowPadClass,
  type Selection,
  type StudioChannel,
  type StudioState,
} from './studioModel';

/** Real community data for the preview — null for a blank-theme template. */
export interface StudioLiveData {
  members: ViewMember[];
  messages: ViewMessage[];
  memberCount: number;
}

function canvasBackground(state: StudioState): CSSProperties {
  const overlay = dimOverlay(state.dim);
  if (state.themeId === 'custom') {
    return state.customImage
      ? { backgroundImage: `${overlay}, url(${state.customImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
      : { background: 'linear-gradient(135deg,#16161c,#0a0a0e)' };
  }
  const url = themeAssetUrl(getTheme(state.themeId)?.file);
  if (!url) return { background: 'linear-gradient(160deg,#16161a,#0d0d10)' };
  return { backgroundImage: `${overlay}, url(${url})`, backgroundSize: 'cover', backgroundPosition: 'center' };
}

const channelOf = (m: ViewMessage) => m.channel_id || 'general';

interface StudioCanvasProps {
  state: StudioState;
  selected: Selection;
  onSelect: (sel: Selection) => void;
  live: StudioLiveData | null;
  zoom: number;
}

/**
 * The editable workspace — a faithful wireframe of the real in-community view
 * (rail + chat + members). When editing a real community it renders that
 * community's live data (member counts, members, messages); a blank theme shows
 * neutral template placeholders. The wireframe STRUCTURE is fixed; clicking a
 * region selects it for the inspector.
 */
export function StudioCanvas({ state, selected, onSelect, live, zoom }: StudioCanvasProps) {
  const radius = radiusClass(state.corners);
  const rowPad = rowPadClass(state.density);
  const groups = groupStudioChannels(state.channels);
  // Match the live view's default open channel (#general), not the first (announcements/pinned).
  const active =
    state.channels.find((c) => c.id === 'general') ??
    state.channels.find((c) => c.kind === 'text') ??
    state.channels[0];

  const online = live ? live.members.filter((m) => m.online).length : 32;
  const total = live ? Math.max(live.memberCount, live.members.length, online) : 1248;

  // Effects
  const glowShadow = state.glow ? `0 0 14px ${state.accent}88` : undefined;
  const dotAnim = state.animated ? 'animate-cv2-breath' : '';
  const nameStyle: CSSProperties =
    state.nameStyle === 'gradient'
      ? { backgroundImage: `linear-gradient(90deg, ${state.accent}, #ffffff)`, WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }
      : { color: '#fff' };

  return (
    <main className="relative flex min-w-0 flex-1 overflow-auto bg-ink-950 p-4" onClick={() => onSelect('page')}>
      {/* Artboard scale — CSS zoom reflows layout so hit-targets stay correct. */}
      <div className="flex min-h-full w-full flex-1" style={{ zoom }}>
      <div
        className={cn(
          'relative flex min-h-[560px] w-full flex-1 flex-col overflow-hidden rounded-2xl border transition-shadow',
          fontClass(state.font),
          selected === 'page' ? 'border-transparent shadow-[0_0_0_2px_var(--studio-accent)]' : 'border-white/10',
        )}
        style={{ ...canvasBackground(state), '--studio-accent': state.accent } as CSSProperties}
      >
        {state.bgTint !== 'off' && (
          <div
            className="pointer-events-none absolute inset-0"
            style={{ background: state.accent, opacity: bgTintOpacity(state.bgTint), mixBlendMode: 'soft-light' }}
          />
        )}
        {state.vignette && (
          <div className="pointer-events-none absolute inset-0" style={{ boxShadow: 'inset 0 0 140px 50px rgba(0,0,0,0.55)' }} />
        )}
        <div className={cn('relative z-[1] m-3 flex min-h-0 flex-1 flex-col overflow-hidden text-white', cardClass(state.cardStyle), radius)}>
          {/* Top bar — center tabs + actions */}
          <div className="flex h-12 flex-none items-center justify-between border-b border-white/[0.06] px-3">
            <div className="flex items-center gap-1 text-white/40">
              <Dot /> <Dot /> <Dot />
            </div>
            <div className="flex items-center gap-1 text-[12px] font-semibold">
              <Tab active>
                <MessageCircle size={13} /> Chat
              </Tab>
              <Tab>
                <Pin size={13} /> Pinned
              </Tab>
              <Tab>
                <Users size={13} /> Members
              </Tab>
            </div>
            <div className="flex items-center gap-2 text-white/40">
              <Search size={14} />
              <Plus size={14} />
            </div>
          </div>

          {/* Body — fixed 3-pane wireframe */}
          <div
            className="grid min-h-0 flex-1"
            style={{ gridTemplateColumns: `${railWidthPx(state.railWidth)} 1fr 180px` }}
          >
            {/* ── Left rail ─────────────────────────────────────── */}
            <div className="flex min-h-0 flex-col border-r border-white/[0.06]">
              <Region label="Community" active={selected === 'community'} onSelect={() => onSelect('community')}>
                {state.bannerDesign?.layers.length ? (
                  <div className="relative h-16">
                    <BannerCoverRender design={state.bannerDesign} focal={state.bannerFocal} />
                  </div>
                ) : (
                  <div
                    className="h-16 bg-cover"
                    style={
                      state.banner
                        ? {
                            backgroundImage: `url(${state.banner})`,
                            backgroundPosition: `${state.bannerFocal.x}% ${state.bannerFocal.y}%`,
                          }
                        : { background: `linear-gradient(120deg, ${state.accent}, rgba(0,0,0,0.15))` }
                    }
                  />
                )}
                <div className="px-3 pb-3 pt-2.5">
                  <div className="truncate text-[14px] font-extrabold" style={nameStyle}>
                    {state.communityName || 'Your Community'}
                  </div>
                  <div className="mt-1 flex items-center gap-1.5 text-[10px] text-white/50">
                    <span className={cn('h-1.5 w-1.5 rounded-full bg-[#22c55e]', dotAnim)} />
                    <b className="font-bold text-white/80">{fmtCount(online)}</b> online
                    <span className="h-1 w-1 rounded-full bg-white/25" />
                    <b className="font-bold text-white/80">{fmtCount(total)}</b> members
                  </div>
                  <button
                    type="button"
                    className={cn('mt-2.5 flex h-7 w-full items-center justify-center gap-1.5 text-[11px] font-bold text-black', radius)}
                    style={{ background: state.accent, boxShadow: glowShadow }}
                  >
                    {state.joinLabel || 'Join'} <ArrowRight size={12} strokeWidth={2.6} />
                  </button>
                </div>
              </Region>

              <Region
                label="Channels"
                active={selected === 'channels'}
                onSelect={() => onSelect('channels')}
                className="no-scrollbar min-h-0 flex-1 overflow-y-auto px-2 pb-3"
              >
                {groups.map((group) => (
                  <ChannelGroup
                    key={group.section}
                    label={group.section}
                    channels={group.items}
                    activeId={active?.id}
                    accent={state.accent}
                    radius={radius}
                    rowPad={rowPad}
                    glowShadow={glowShadow}
                    dotAnim={dotAnim}
                  />
                ))}
              </Region>
            </div>

            {/* ── Center chat ───────────────────────────────────── */}
            <Region label="Chat" active={selected === 'chat'} onSelect={() => onSelect('chat')} className="flex min-h-0 flex-col">
              <div className="flex h-11 flex-none items-center gap-2 border-b border-white/[0.06] px-4">
                <Hash size={15} className="text-white/45" />
                <span className="text-[13px] font-bold text-white">{active?.name ?? 'general'}</span>
              </div>
              <ChatMessages
                live={live}
                active={active}
                accent={state.accent}
                density={state.density}
                bubble={state.messageStyle === 'bubble'}
              />
              <div className="flex-none px-3 pb-3">
                <div className={cn('flex items-center gap-2 border border-white/10 bg-black/30 px-3 py-2', radius)}>
                  <Plus size={15} className="text-white/40" />
                  <span className="flex-1 text-[12px] text-white/35">Message #{active?.name ?? 'general'}</span>
                  <Smile size={15} className="text-white/40" />
                  <span className={cn('grid h-6 w-6 place-items-center text-black', radius)} style={{ background: state.accent, boxShadow: glowShadow }}>
                    <Send size={12} />
                  </span>
                </div>
              </div>
            </Region>

            {/* ── Members ───────────────────────────────────────── */}
            <Region label="Members" active={selected === 'members'} onSelect={() => onSelect('members')} className="min-h-0 overflow-y-auto border-l border-white/[0.06] px-3 py-3">
              <MembersPanel live={live} accent={state.accent} rowPad={rowPad} dotAnim={dotAnim} />
            </Region>
          </div>
        </div>
      </div>
      </div>
    </main>
  );
}

function ChatMessages({
  live,
  active,
  accent,
  density,
  bubble,
}: {
  live: StudioLiveData | null;
  active: StudioChannel | undefined;
  accent: string;
  density: StudioState['density'];
  bubble: boolean;
}) {
  const gap = cn('min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4', density === 'tight' && 'space-y-2', density === 'roomy' && 'space-y-5');
  const name = active?.name ?? 'general';
  const bodyClass = cn(
    'text-[12px] leading-relaxed text-white/75',
    bubble && 'mt-1 inline-block rounded-xl rounded-tl-sm border border-white/10 bg-white/[0.06] px-2.5 py-1.5',
  );

  if (live) {
    const msgs = live.messages.filter((m) => channelOf(m) === (active?.id ?? 'general'));
    if (msgs.length === 0) {
      return (
        <div className={cn(gap, 'flex flex-col items-center justify-center text-center')}>
          <p className="text-[13px] font-bold text-white/80">This is the beginning of #{name}.</p>
          <p className="mt-1 text-[12px] text-white/40">No messages in #{name} yet.</p>
        </div>
      );
    }
    return (
      <div className={gap}>
        {msgs.slice(-12).map((m) => (
          <div key={m.id} className="flex gap-2.5">
            <Avatar src={m.author?.avatar_url} name={displayName(m.author)} size={28} className="mt-0.5 flex-none" />
            <div className="min-w-0">
              <span className="text-[12px] font-bold text-white">{displayName(m.author)}</span>
              <p className={bodyClass}>{m.body || (m.mediaUrl ? '📎 attachment' : '')}</p>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Blank-theme template
  return (
    <div className={gap}>
      {SAMPLE_MESSAGES.map((m, i) => (
        <div key={i} className="flex gap-2.5">
          <span className="h-7 w-7 flex-none rounded-full" style={{ background: `linear-gradient(135deg, ${accent}, rgba(255,255,255,0.18))` }} />
          <div className="min-w-0">
            <div className="flex items-baseline gap-2">
              <span className="text-[12px] font-bold text-white">{m.name}</span>
              <span className="text-[10px] text-white/35">{m.time}</span>
            </div>
            <p className={bodyClass}>{m.body}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function MembersPanel({ live, accent, rowPad, dotAnim }: { live: StudioLiveData | null; accent: string; rowPad: string; dotAnim: string }) {
  if (live) {
    const onlineM = live.members.filter((m) => m.online);
    const offlineM = live.members.filter((m) => !m.online);
    return (
      <>
        <RealMemberGroup label={`Online — ${onlineM.length}`} members={onlineM} rowPad={rowPad} dotAnim={dotAnim} online />
        {offlineM.length > 0 && <RealMemberGroup label={`Offline — ${offlineM.length}`} members={offlineM} rowPad={rowPad} dotAnim={dotAnim} />}
        {live.members.length === 0 && <p className="px-1 pt-2 text-[11px] text-white/40">No members yet.</p>}
      </>
    );
  }
  return (
    <>
      <SampleMemberGroup label="Online — 3" accent={accent} online count={3} rowPad={rowPad} dotAnim={dotAnim} />
      <SampleMemberGroup label="Offline — 2" accent={accent} count={2} rowPad={rowPad} dotAnim={dotAnim} />
    </>
  );
}

function RealMemberGroup({ label, members, rowPad, online, dotAnim }: { label: string; members: ViewMember[]; rowPad: string; online?: boolean; dotAnim: string }) {
  return (
    <div className="mb-2">
      <div className="px-1 pb-1 pt-2 text-[9px] font-bold uppercase tracking-[0.7px] text-white/45">{label}</div>
      {members.slice(0, 12).map((m) => (
        <div key={m.user_id} className={cn('flex items-center gap-2 px-1', rowPad, !online && 'opacity-50')}>
          <span className="relative flex-none">
            <Avatar src={m.profile?.avatar_url} name={displayName(m.profile)} size={24} />
            {online && <span className={cn('absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full border border-black bg-[#22c55e]', dotAnim)} />}
          </span>
          <span className="min-w-0 flex-1 truncate text-[11px] font-semibold text-white/80">{displayName(m.profile)}</span>
        </div>
      ))}
    </div>
  );
}

function Region({
  label,
  active,
  onSelect,
  className,
  children,
}: {
  label: string;
  active: boolean;
  onSelect: () => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      className={cn(
        'relative cursor-pointer transition',
        className,
        active
          ? 'z-10 outline outline-2 -outline-offset-2 outline-[var(--studio-accent)]'
          : 'hover:outline hover:outline-1 hover:-outline-offset-1 hover:outline-white/20',
      )}
    >
      {active && (
        <span className="absolute right-1 top-1 z-20 rounded px-1 py-0.5 text-[9px] font-bold text-black" style={{ background: 'var(--studio-accent)' }}>
          {label}
        </span>
      )}
      {children}
    </div>
  );
}

function ChannelGroup({
  label,
  channels,
  activeId,
  accent,
  radius,
  rowPad,
  glowShadow,
  dotAnim,
}: {
  label: string;
  channels: StudioChannel[];
  activeId: string | undefined;
  accent: string;
  radius: string;
  rowPad: string;
  glowShadow: string | undefined;
  dotAnim: string;
}) {
  if (channels.length === 0) return null;
  return (
    <div>
      <div className="px-2 pb-1 pt-3 text-[9px] font-bold uppercase tracking-[0.7px] text-white/45">{label}</div>
      {channels.map((ch) => {
        const isActive = ch.id === activeId;
        const Icon = iconById(ch.icon);
        return (
          <div
            key={ch.id}
            className={cn('relative my-0.5 flex items-center gap-2 px-2 text-[12px] font-medium', rowPad, radius, isActive ? 'text-white' : 'text-white/70')}
            style={isActive ? { background: `${accent}24`, boxShadow: glowShadow } : undefined}
          >
            <ChannelFx effect={ch.effect} />
            {isActive && (
              <span
                className={cn('absolute -left-0.5 bottom-1.5 top-1.5 w-[3px] rounded-r', dotAnim)}
                style={{ background: accent, boxShadow: glowShadow }}
              />
            )}
            <Icon size={15} className="flex-none" style={{ color: ch.kind === 'voice' ? '#4ade80' : isActive ? accent : undefined }} />
            <span className="min-w-0 flex-1 truncate">{ch.name || 'channel'}</span>
          </div>
        );
      })}
    </div>
  );
}

function SampleMemberGroup({
  label,
  accent,
  online,
  count,
  rowPad,
  dotAnim,
}: {
  label: string;
  accent: string;
  online?: boolean;
  count: number;
  rowPad: string;
  dotAnim: string;
}) {
  return (
    <div className="mb-2">
      <div className="px-1 pb-1 pt-2 text-[9px] font-bold uppercase tracking-[0.7px] text-white/45">{label}</div>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className={cn('flex items-center gap-2 px-1', rowPad, !online && 'opacity-50')}>
          <span className="relative h-6 w-6 flex-none rounded-full" style={{ background: `linear-gradient(135deg, ${accent}, rgba(255,255,255,0.18))` }}>
            {online && <span className={cn('absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full border border-black bg-[#22c55e]', dotAnim)} />}
          </span>
          <span className="h-2 flex-1 rounded-full bg-white/15" />
        </div>
      ))}
    </div>
  );
}

function Tab({ active, children }: { active?: boolean; children: ReactNode }) {
  return (
    <span className={cn('relative inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5', active ? 'text-white' : 'text-white/45')}>
      {children}
      {active && <span className="absolute inset-x-2.5 -bottom-[7px] h-0.5 rounded-full bg-white/85" />}
    </span>
  );
}

function Dot() {
  return <span className="h-1 w-1 rounded-full bg-current" />;
}

const SAMPLE_MESSAGES = [
  { name: 'Maya', time: '10:24', body: 'just shipped the new onboarding flow 🚀' },
  { name: 'Devon', time: '10:26', body: 'huge — the retention numbers are gonna love that' },
  { name: 'Priya', time: '10:31', body: 'who’s hopping in the voice channel for standup?' },
];
